import * as React from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { api, getErrorMessage } from '@/api/api';
import { BASE_URL } from '@/api/client';
import { API_DOCS_PATH } from '@/routes/paths';
import type {
  DeveloperApiKey,
  DeveloperApiKeyCreateResponse,
  DeveloperRecentRequest,
  DeveloperUsageDay,
  DeveloperUsageSummary,
} from '@/types';

const EMPTY_USAGE: DeveloperUsageSummary = {
  period_days: 30,
  active_keys: 0,
  total_requests: 0,
  successful_requests: 0,
  failed_requests: 0,
  credits_charged: 0,
  prompt_tokens: 0,
  completion_tokens: 0,
  total_tokens: 0,
  estimated_cost_usd: 0,
  average_latency_ms: 0,
  daily: [],
  by_key: [],
  recent_requests: [],
};

function formatDate(value?: string | null): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function statusClass(status: DeveloperApiKey['status']) {
  return status === 'active'
    ? 'bg-emerald-50 text-emerald-700 ring-emerald-100'
    : 'bg-gray-100 text-gray-500 ring-gray-200';
}

type UsageMetric = 'requests' | 'credits_charged' | 'tokens' | 'errors';

interface UsagePoint extends DeveloperUsageDay {
  label: string;
  fullLabel: string;
}

const USAGE_METRICS: Record<
  UsageMetric,
  {
    label: string;
    shortLabel: string;
    help: string;
    barClass: string;
    activeClass: string;
  }
> = {
  requests: {
    label: 'Requests',
    shortLabel: 'calls',
    help: 'How many times an app called the chat API.',
    barClass: 'bg-brand-800',
    activeClass: 'border-brand-800 bg-brand-800 text-white',
  },
  credits_charged: {
    label: 'Credits',
    shortLabel: 'credits',
    help: 'Account credits spent by API conversations.',
    barClass: 'bg-emerald-500',
    activeClass: 'border-emerald-600 bg-emerald-600 text-white',
  },
  tokens: {
    label: 'Tokens',
    shortLabel: 'tokens',
    help: 'Estimated text volume from user messages and echo replies.',
    barClass: 'bg-sky-500',
    activeClass: 'border-sky-600 bg-sky-600 text-white',
  },
  errors: {
    label: 'Errors',
    shortLabel: 'errors',
    help: 'Calls that did not complete successfully.',
    barClass: 'bg-red-500',
    activeClass: 'border-red-500 bg-red-500 text-white',
  },
};

function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getUsageValue(point: DeveloperUsageDay, metric: UsageMetric): number {
  if (metric === 'tokens') {
    return (point.prompt_tokens ?? 0) + (point.completion_tokens ?? 0);
  }

  return point[metric] ?? 0;
}

function formatCount(value: number, label: string): string {
  const suffix = value === 1 ? label.replace(/s$/, '') : label;
  return `${value.toLocaleString()} ${suffix}`;
}

function buildUsageSeries(daily: DeveloperUsageDay[], periodDays: number): UsagePoint[] {
  const days = Math.max(1, Math.min(periodDays, 365));
  const byDate = new Map(daily.map((day) => [day.date, day]));
  const formatter = new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' });
  const fullFormatter = new Intl.DateTimeFormat('en', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  const today = new Date();

  return Array.from({ length: days }, (_, index) => {
    const date = new Date(today);
    date.setHours(0, 0, 0, 0);
    date.setDate(today.getDate() - (days - index - 1));

    const key = toDateKey(date);
    const value = byDate.get(key);
    return {
      date: key,
      requests: value?.requests ?? 0,
      credits_charged: value?.credits_charged ?? 0,
      errors: value?.errors ?? 0,
      prompt_tokens: value?.prompt_tokens ?? 0,
      completion_tokens: value?.completion_tokens ?? 0,
      label: formatter.format(date),
      fullLabel: fullFormatter.format(date),
    };
  });
}

function sumMetric(points: DeveloperUsageDay[], metric: UsageMetric): number {
  return points.reduce((total, point) => total + getUsageValue(point, metric), 0);
}

function getTrendText(current: number, previous: number, label: string): string {
  if (current === 0 && previous === 0) {
    return `No ${label} in the last two weeks.`;
  }
  if (previous === 0) {
    return current > 0
      ? `Started this week with ${formatCount(current, label)}.`
      : `No ${label} this week.`;
  }

  const diff = current - previous;
  const percent = Math.round((Math.abs(diff) / previous) * 100);
  if (diff === 0) return 'Same as the previous 7 days.';
  return `${percent}% ${diff > 0 ? 'higher' : 'lower'} than the previous 7 days.`;
}

const UsageGraph = ({ usage }: { usage: DeveloperUsageSummary }) => {
  const [metric, setMetric] = React.useState<UsageMetric>('requests');
  const series = React.useMemo(
    () => buildUsageSeries(usage.daily, usage.period_days),
    [usage.daily, usage.period_days]
  );

  const metricConfig = USAGE_METRICS[metric];
  const maxValue = Math.max(1, ...series.map((point) => getUsageValue(point, metric)));
  const total = sumMetric(series, metric);
  const currentWeek = series.slice(-7);
  const previousWeek = series.slice(-14, -7);
  const currentWeekTotal = sumMetric(currentWeek, metric);
  const previousWeekTotal = sumMetric(previousWeek, metric);
  const peakPoint = series.reduce(
    (peak, point) => (getUsageValue(point, metric) >= getUsageValue(peak, metric) ? point : peak),
    series[0]
  );
  const trendText = getTrendText(currentWeekTotal, previousWeekTotal, metricConfig.shortLabel);

  return (
    <section className='rounded-lg border border-gray-200 bg-white p-5'>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
        <div>
          <h3 className='font-sans text-[22px] font-bold text-brand-800'>Usage at a glance</h3>
          <p className='mt-1 text-14 text-gray-500'>
            Last {usage.period_days} days of API activity. Pick the number you want to understand.
          </p>
        </div>
        <div className='flex flex-wrap gap-2'>
          {(Object.keys(USAGE_METRICS) as UsageMetric[]).map((key) => {
            const option = USAGE_METRICS[key];
            const isActive = metric === key;
            return (
              <button
                key={key}
                type='button'
                onClick={() => setMetric(key)}
                className={clsx(
                  'rounded-30 border px-4 py-2 text-13 font-bold transition-colors',
                  isActive ? option.activeClass : 'border-gray-200 bg-white text-brand-800'
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className='mt-5 grid grid-cols-1 gap-3 text-14 md:grid-cols-3'>
        <div className='rounded-lg bg-gray-75 p-4'>
          <div className='font-bold text-brand-800'>Last 7 days</div>
          <div className='mt-1 text-gray-600'>
            {formatCount(currentWeekTotal, metricConfig.shortLabel)}
          </div>
        </div>
        <div className='rounded-lg bg-gray-75 p-4'>
          <div className='font-bold text-brand-800'>Compared with last week</div>
          <div className='mt-1 text-gray-600'>{trendText}</div>
        </div>
        <div className='rounded-lg bg-gray-75 p-4'>
          <div className='font-bold text-brand-800'>Busiest day</div>
          <div className='mt-1 text-gray-600'>
            {total > 0
              ? `${peakPoint.fullLabel}: ${formatCount(
                  getUsageValue(peakPoint, metric),
                  metricConfig.shortLabel
                )}`
              : 'No activity yet.'}
          </div>
        </div>
      </div>

      <p className='mt-4 text-13 text-gray-500'>{metricConfig.help}</p>

      <div className='mt-5 overflow-x-auto pb-2'>
        <div className='min-w-[720px]'>
          <div
            className='grid h-56 items-end gap-2 rounded-lg bg-gray-75 p-4'
            style={{ gridTemplateColumns: `repeat(${series.length}, minmax(14px, 1fr))` }}
          >
            {series.map((point, index) => {
              const value = getUsageValue(point, metric);
              const height = value > 0 ? Math.max(8, Math.round((value / maxValue) * 100)) : 2;
              const shouldLabel = index === 0 || index === series.length - 1 || index % 5 === 0;

              return (
                <div
                  key={point.date}
                  className='flex h-full min-w-0 flex-col items-center justify-end'
                  title={`${point.fullLabel}: ${formatCount(value, metricConfig.shortLabel)}`}
                >
                  <div className='mb-1 h-4 text-[10px] font-bold text-gray-500'>
                    {value > 0 ? value : ''}
                  </div>
                  <div
                    className={clsx(
                      'w-full rounded-t-md transition-all',
                      value > 0 ? metricConfig.barClass : 'bg-gray-200'
                    )}
                    style={{ height: `${height}%` }}
                    aria-label={`${point.fullLabel}: ${formatCount(
                      value,
                      metricConfig.shortLabel
                    )}`}
                  />
                  <div className='mt-2 h-8 text-center text-[10px] leading-3 text-gray-500'>
                    {shouldLabel ? point.label : ''}
                  </div>
                </div>
              );
            })}
          </div>
          {total === 0 && (
            <div className='mt-3 rounded-lg border border-dashed border-gray-200 px-4 py-3 text-center text-13 text-gray-500'>
              API usage will appear here after the first request.
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);
}

function formatTokens(value: number): string {
  return `${value.toLocaleString()} est. tokens`;
}

const StatCard = ({
  label,
  value,
  help,
}: {
  label: string;
  value: string | number;
  help?: string;
}) => (
  <div className='rounded-lg border border-gray-200 bg-white px-5 py-4'>
    <div className='font-sans text-13 font-semibold uppercase tracking-wide text-gray-500'>
      {label}
    </div>
    <div className='mt-2 font-jakarta text-[28px] font-bold text-brand-800'>{value}</div>
    {help && <p className='mt-1 text-12 text-gray-500'>{help}</p>}
  </div>
);

const RequestRow = ({ request }: { request: DeveloperRecentRequest }) => (
  <div className='grid grid-cols-1 gap-2 border-b border-gray-100 py-3 text-13 text-gray-600 last:border-b-0 md:grid-cols-[minmax(0,1fr)_70px_80px_90px_80px] md:items-center'>
    <div className='min-w-0'>
      <div className='truncate font-semibold text-brand-800'>{request.endpoint}</div>
      <div className='truncate'>
        {request.request_id} - {formatDate(request.created_at)}
      </div>
    </div>
    <div
      className={clsx(
        'font-semibold',
        request.status_code >= 400 ? 'text-red-500' : 'text-emerald-600'
      )}
    >
      {request.status_code}
    </div>
    <div>{request.credits_charged} credits</div>
    <div>{formatTokens((request.prompt_tokens ?? 0) + (request.completion_tokens ?? 0))}</div>
    <div className='md:text-right'>{request.latency_ms} ms</div>
  </div>
);

const UsageMeaningPanel = ({ usage }: { usage: DeveloperUsageSummary }) => (
  <section className='rounded-lg border border-gray-200 bg-white p-5'>
    <div className='flex flex-col gap-2'>
      <h3 className='font-sans text-[22px] font-bold text-brand-800'>Usage and cost</h3>
      <p className='max-w-3xl text-14 text-gray-500'>
        Account-private API totals for the last {usage.period_days} days. These numbers are for
        API-key traffic only, not normal web app chat.
      </p>
    </div>

    <div className='mt-5 grid grid-cols-1 gap-4 lg:grid-cols-4'>
      <div className='rounded-lg bg-gray-75 p-4'>
        <div className='font-bold text-brand-800'>Requests</div>
        <p className='mt-1 text-13 text-gray-600'>
          Every API call your integration makes, including calls that fail.
        </p>
      </div>
      <div className='rounded-lg bg-gray-75 p-4'>
        <div className='font-bold text-brand-800'>Credits</div>
        <p className='mt-1 text-13 text-gray-600'>
          Billable account usage. Authenticated validation, quota, and rate-limit failures charge 0
          credits.
        </p>
      </div>
      <div className='rounded-lg bg-gray-75 p-4'>
        <div className='font-bold text-brand-800'>Tokens</div>
        <p className='mt-1 text-13 text-gray-600'>
          Estimated text volume from saved character counts. It is useful for trend tracking.
        </p>
      </div>
      <div className='rounded-lg bg-gray-75 p-4'>
        <div className='font-bold text-brand-800'>Estimated dollars</div>
        <p className='mt-1 text-13 text-gray-600'>
          Approximate spend from credits charged and average credit package price.
        </p>
      </div>
    </div>

    <div className='mt-5 rounded-lg border border-sky-100 bg-sky-50 px-4 py-3 text-14 text-sky-800'>
      1 request is not always 1 credit. Today, a successful text chat usually charges the normal
      text-message cost, which is commonly 1 credit. Errors still count as requests, but they do not
      spend credits.
    </div>
  </section>
);

export const DeveloperTab: React.FC = () => {
  const [keys, setKeys] = React.useState<DeveloperApiKey[]>([]);
  const [usage, setUsage] = React.useState<DeveloperUsageSummary>(EMPTY_USAGE);
  const [newKeyName, setNewKeyName] = React.useState('Production key');
  const [createdKey, setCreatedKey] = React.useState<DeveloperApiKeyCreateResponse | null>(null);
  const [copied, setCopied] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isCreating, setIsCreating] = React.useState(false);

  const loadDeveloperData = React.useCallback(async () => {
    setError(null);
    try {
      const [keyResponse, usageResponse] = await Promise.all([
        api.developer.listKeys(),
        api.developer.getUsage(30),
      ]);
      setKeys(keyResponse.api_keys);
      setUsage(usageResponse);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadDeveloperData();
  }, [loadDeveloperData]);

  const copyToClipboard = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    window.setTimeout(() => setCopied(null), 1800);
  };

  const handleCreate = async () => {
    const name = newKeyName.trim();
    if (!name) return;

    setIsCreating(true);
    setError(null);
    try {
      const response = await api.developer.createKey(name);
      setCreatedKey(response);
      setNewKeyName('');
      await loadDeveloperData();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevoke = async (keyId: string) => {
    setError(null);
    try {
      await api.developer.revokeKey(keyId);
      await loadDeveloperData();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleRotate = async (keyId: string) => {
    setError(null);
    try {
      const response = await api.developer.rotateKey(keyId);
      setCreatedKey(response);
      await loadDeveloperData();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  if (isLoading) {
    return <div className='py-8 font-sans text-gray-500'>Loading developer settings...</div>;
  }

  return (
    <div className='animate-slide-in flex w-full flex-col gap-8 pt-2'>
      {error && (
        <div className='rounded-lg bg-red-50 px-4 py-3 text-14 font-semibold text-red-600'>
          {error}
        </div>
      )}

      {createdKey && (
        <div className='rounded-lg border border-emerald-100 bg-emerald-50 p-5'>
          <div className='font-sans text-18 font-bold text-emerald-800'>New API key created</div>
          <div className='mt-1 text-14 text-emerald-700'>
            This is the only time the full key is shown.
          </div>
          <div className='mt-4 flex flex-col gap-3 rounded-lg bg-white p-4 md:flex-row md:items-center'>
            <code className='min-w-0 flex-1 overflow-x-auto whitespace-nowrap font-mono text-13 text-brand-800'>
              {createdKey.api_key}
            </code>
            <button
              type='button'
              onClick={() => copyToClipboard(createdKey.api_key, 'key')}
              className='rounded-30 bg-brand-800 px-5 py-2 text-14 font-bold text-white'
            >
              {copied === 'key' ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      )}

      <div className='grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5'>
        <StatCard
          label='API Requests'
          value={usage.total_requests.toLocaleString()}
          help={`${usage.successful_requests.toLocaleString()} successful, ${usage.failed_requests.toLocaleString()} failed`}
        />
        <StatCard
          label='Credits Used'
          value={usage.credits_charged.toLocaleString()}
          help='Billable account credits'
        />
        <StatCard
          label='Estimated Tokens'
          value={usage.total_tokens.toLocaleString()}
          help={`${usage.prompt_tokens.toLocaleString()} prompt + ${usage.completion_tokens.toLocaleString()} reply`}
        />
        <StatCard
          label='Estimated Spend'
          value={formatUsd(usage.estimated_cost_usd)}
          help='Not an invoice'
        />
        <StatCard
          label='Avg Latency'
          value={`${usage.average_latency_ms} ms`}
          help='Completed and failed calls'
        />
      </div>

      <UsageMeaningPanel usage={usage} />

      <UsageGraph usage={usage} />

      <div className='grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1fr)_420px]'>
        <div className='flex min-w-0 flex-col gap-6'>
          <section className='rounded-lg border border-gray-200 bg-white p-5'>
            <div className='flex flex-col gap-4 md:flex-row md:items-end md:justify-between'>
              <div>
                <h3 className='font-sans text-[22px] font-bold text-brand-800'>API Keys</h3>
                <p className='mt-1 text-14 text-gray-500'>
                  Keys inherit your account credits and are limited per key.
                </p>
              </div>
              <div className='flex w-full gap-3 md:w-auto'>
                <input
                  value={newKeyName}
                  onChange={(event) => setNewKeyName(event.target.value)}
                  placeholder='Key name'
                  className='h-11 min-w-0 flex-1 rounded-lg border border-gray-200 px-3 font-sans text-14 text-brand-800 outline-none focus:border-brand-300 md:w-56'
                />
                <button
                  type='button'
                  onClick={handleCreate}
                  disabled={isCreating || !newKeyName.trim()}
                  className='h-11 rounded-30 bg-brand-800 px-6 text-14 font-bold text-white disabled:opacity-50'
                >
                  {isCreating ? 'Creating' : 'Create'}
                </button>
              </div>
            </div>

            <div className='mt-5 divide-y divide-gray-100'>
              {keys.length === 0 ? (
                <div className='py-8 text-center text-14 text-gray-500'>No API keys yet.</div>
              ) : (
                keys.map((key) => (
                  <div
                    key={key.id}
                    className='flex flex-col gap-4 py-4 md:flex-row md:items-center'
                  >
                    <div className='min-w-0 flex-1'>
                      <div className='flex flex-wrap items-center gap-2'>
                        <span className='font-sans text-16 font-bold text-brand-800'>
                          {key.name}
                        </span>
                        <span
                          className={clsx(
                            'rounded-full px-2 py-0.5 text-12 font-bold ring-1',
                            statusClass(key.status)
                          )}
                        >
                          {key.status}
                        </span>
                      </div>
                      <div className='mt-1 font-mono text-13 text-gray-500'>{key.display_key}</div>
                      <div className='mt-1 text-12 text-gray-500'>
                        Created {formatDate(key.created_at)} · Last used{' '}
                        {formatDate(key.last_used_at)}
                      </div>
                    </div>
                    <div className='flex shrink-0 gap-2'>
                      {key.status === 'active' && (
                        <>
                          <button
                            type='button'
                            onClick={() => handleRotate(key.id)}
                            className='rounded-30 border border-gray-200 px-4 py-2 text-13 font-bold text-brand-800'
                          >
                            Rotate
                          </button>
                          <button
                            type='button'
                            onClick={() => handleRevoke(key.id)}
                            className='rounded-30 border border-red-100 px-4 py-2 text-13 font-bold text-red-600'
                          >
                            Revoke
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className='rounded-lg border border-gray-200 bg-white p-5'>
            <h3 className='font-sans text-[22px] font-bold text-brand-800'>Recent Requests</h3>
            <div className='mt-4'>
              {usage.recent_requests.length === 0 ? (
                <div className='py-8 text-center text-14 text-gray-500'>
                  No API traffic in the last 30 days.
                </div>
              ) : (
                usage.recent_requests.map((request) => (
                  <RequestRow key={request.request_id} request={request} />
                ))
              )}
            </div>
          </section>
        </div>

        <aside className='flex flex-col gap-6'>
          <section className='rounded-lg border border-gray-200 bg-white p-5'>
            <h3 className='font-sans text-[22px] font-bold text-brand-800'>API access</h3>
            <div className='mt-4 flex flex-col gap-4 text-14 text-gray-600'>
              <div>
                <span className='font-bold text-brand-800'>Endpoint</span>
                <code className='mt-1 block overflow-x-auto rounded-lg bg-gray-75 p-3 font-mono text-12 text-brand-800'>
                  POST {BASE_URL}/v1/chat/completions
                </code>
              </div>
              <div>
                <span className='font-bold text-brand-800'>Active keys</span>
                <div className='mt-1 rounded-lg bg-gray-75 p-3 font-sans text-14 text-brand-800'>
                  {usage.active_keys.toLocaleString()} active key
                  {usage.active_keys === 1 ? '' : 's'}
                </div>
              </div>
              <p>
                Backend examples, request fields, response fields, and errors are maintained on the
                dedicated API docs page.
              </p>
              <Link
                to={API_DOCS_PATH}
                className='inline-flex h-11 items-center justify-center rounded-30 bg-brand-800 px-6 text-14 font-bold text-white'
              >
                Open API docs
              </Link>
            </div>
          </section>

          <section className='rounded-lg border border-gray-200 bg-white p-5'>
            <h3 className='font-sans text-[22px] font-bold text-brand-800'>Cost estimate</h3>
            <div className='mt-4 rounded-lg bg-gray-75 p-4'>
              <div className='font-jakarta text-[30px] font-bold text-brand-800'>
                {formatUsd(usage.estimated_cost_usd)}
              </div>
              <p className='mt-1 text-13 text-gray-500'>
                Based on {usage.credits_charged.toLocaleString()} credits charged in this period.
              </p>
            </div>
            <div className='mt-4 flex flex-col gap-3 text-14 text-gray-600'>
              <p>Credits are the billing unit. Requests are the traffic count.</p>
              <p>Token totals are estimates from character counts and should be read as trends.</p>
              <p>Use separate keys for staging and production to keep usage easy to audit.</p>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
};
